import { useState, useEffect, useMemo } from 'react';
import { fetchTutors } from '../services/api';
import {
  Search, MapPin, Monitor, BookOpen, X,
  GraduationCap, Loader2, Users, SlidersHorizontal, Tag,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ── Helpers ───────────────────────────────────────────────────────────────────
const MODE_STYLE = {
  Online:   'bg-green-500/10 text-green-400 border-green-500/20',
  Physical: 'bg-blue-500/10  text-blue-400  border-blue-500/20',
  Both:     'bg-purple-500/10 text-purple-400 border-purple-500/20',
};
const SYLLABUS_STYLE = {
  Local:     'bg-green-500/10 text-green-400 border-green-500/20',
  Cambridge: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  Edexcel:   'bg-orange-500/10 text-orange-400 border-orange-500/20',
};
const AVATAR_COLORS = [
  'bg-accentBlue/20 text-accentBlue',
  'bg-purple-500/20 text-purple-400',
  'bg-green-500/20 text-green-400',
  'bg-orange-500/20 text-orange-400',
  'bg-pink-500/20 text-pink-400',
];

const getTutorName = (t) =>
  t.firstName ? `${t.firstName} ${t.lastName || ''}`.trim() : t.name || 'Unknown';

const stagger = { show: { transition: { staggerChildren: 0.07 } } };
const cardAnim = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

// ── Tutor Card ────────────────────────────────────────────────────────────────
const TutorCard = ({ tutor, idx }) => {
  const name     = getTutorName(tutor);
  const initial  = name.charAt(0).toUpperCase();
  const avatar   = AVATAR_COLORS[idx % AVATAR_COLORS.length];
  const subjects = (tutor.subjects || []).map(s => s.subjectName).filter(Boolean);

  return (
    <motion.div variants={cardAnim}
      className="bg-darkBg border border-white/10 rounded-2xl p-6 hover:border-accentBlue/25 hover:shadow-lg hover:shadow-accentBlue/5 transition-all duration-300 flex flex-col">

      {/* Header */}
      <div className="flex items-start gap-4 mb-5">
        <div className={`w-14 h-14 rounded-2xl ${avatar} flex items-center justify-center text-2xl font-extrabold shrink-0`}>
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white text-lg leading-tight truncate">{name}</h3>
          {tutor.username && (
            <p className="text-xs text-gray-500 font-mono mt-0.5">@{tutor.username}</p>
          )}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {/* Syllabus badges */}
            {(tutor.syllabus || []).map(s => (
              <span key={s} className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${SYLLABUS_STYLE[s] || 'bg-white/10 text-gray-300 border-white/20'}`}>{s}</span>
            ))}
            {/* Mode badge */}
            {tutor.mode && (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${MODE_STYLE[tutor.mode] || 'bg-white/10 text-gray-300 border-white/20'}`}>
                {tutor.mode}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Subjects */}
      {subjects.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <BookOpen size={9} /> Subjects
          </p>
          <div className="flex flex-wrap gap-1.5">
            {subjects.map(s => (
              <span key={s} className="text-xs px-2.5 py-1 bg-accentBlue/8 border border-accentBlue/15 text-accentBlue rounded-full font-medium">{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Info */}
      <div className="space-y-2 mb-5 flex-1">
        {tutor.area && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <MapPin size={13} className="text-gray-500 shrink-0" />
            <span>{tutor.area}</span>
          </div>
        )}
        {tutor.mode && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Monitor size={13} className="text-gray-500 shrink-0" />
            <span>Teaching Mode: <span className="text-white font-medium">{tutor.mode}</span></span>
          </div>
        )}
      </div>

      {/* Tags */}
      {(tutor.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-5">
          {tutor.tags.map(tag => (
            <span key={tag} className="text-[10px] px-2 py-0.5 bg-white/8 border border-white/10 text-gray-400 rounded-full flex items-center gap-1">
              <Tag size={8} />{tag}
            </span>
          ))}
        </div>
      )}

      {/* Contact button */}
      <button className="w-full py-2.5 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue text-sm font-bold rounded-xl hover:bg-accentBlue hover:text-black transition-all mt-auto">
        Contact Tutor
      </button>
    </motion.div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const TutorSearchPage = () => {
  const [tutors,  setTutors]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(9);

  const [search,   setSearch]   = useState('');
  const [syllabus, setSyllabus] = useState('');
  const [mode,     setMode]     = useState('');
  const [subject,  setSubject]  = useState('');
  const [area,     setArea]     = useState('');

  useEffect(() => {
    fetchTutors({})
      .then(res => setTutors(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Live local filtering
  const filtered = useMemo(() => {
    const q  = search.toLowerCase();
    const su = subject.toLowerCase();
    const ar = area.toLowerCase();
    return tutors.filter(t => {
      const name = getTutorName(t).toLowerCase();
      if (q  && !name.includes(q)) return false;
      if (syllabus && !(t.syllabus || []).includes(syllabus)) return false;
      if (mode     && t.mode !== mode) return false;
      if (su && !(t.subjects || []).some(s => s.subjectName?.toLowerCase().includes(su))) return false;
      if (ar && !(t.area || '').toLowerCase().includes(ar)) return false;
      return true;
    });
  }, [tutors, search, syllabus, mode, subject, area]);

  const activeCount = [syllabus, mode, subject, area].filter(Boolean).length;
  const clearAll = () => { setSyllabus(''); setMode(''); setSubject(''); setArea(''); };

  return (
    <div className="min-h-screen">

      {/* ── Hero ── */}
      <section className="relative py-16 px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-1/4 w-80 h-80 bg-accentBlue/6 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-1/4 w-60 h-60 bg-purple-500/6 rounded-full blur-[80px]" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <motion.p initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="text-accentBlue text-xs font-bold uppercase tracking-widest mb-4 flex items-center justify-center gap-2">
            <Users size={12} /> Expert Tutors
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight mb-4">
            Find Your{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accentBlue to-blue-300">Perfect Tutor</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }}
            className="text-gray-400 mb-8">
            Browse qualified tutors for Local, Cambridge and Edexcel syllabuses — online or in-person.
          </motion.p>

          {/* Main search */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
            className="relative max-w-lg mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <input
              type="text"
              placeholder="Search by tutor name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-white/8 border border-white/15 py-4 pl-12 pr-12 rounded-2xl text-sm outline-none focus:border-accentBlue focus:ring-2 focus:ring-accentBlue/15 transition-all"
              style={{ color: '#ffffff', caretColor: '#ffffff', backgroundColor: 'rgba(255,255,255,0.06)' }}
            />
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
                <X size={15} />
              </button>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── Filters ── */}
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <div className="bg-white/4 border border-white/10 rounded-2xl p-5">
          <div className="flex flex-wrap items-center gap-4">

            {/* Syllabus chips */}
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Syllabus</p>
              <div className="flex gap-2 flex-wrap">
                {['', 'Local', 'Cambridge', 'Edexcel'].map(s => (
                  <button key={s} onClick={() => setSyllabus(s)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                      syllabus === s
                        ? s ? (SYLLABUS_STYLE[s] + ' !bg-opacity-30') : 'bg-accentBlue/15 text-accentBlue border-accentBlue/30'
                        : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/25 hover:text-white'
                    }`}>
                    {s || 'All'}
                  </button>
                ))}
              </div>
            </div>

            <div className="w-px h-10 bg-white/10 hidden sm:block" />

            {/* Mode chips */}
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Mode</p>
              <div className="flex gap-2 flex-wrap">
                {['', 'Online', 'Physical', 'Both'].map(m => (
                  <button key={m} onClick={() => setMode(m)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                      mode === m
                        ? m ? (MODE_STYLE[m] + ' !bg-opacity-30') : 'bg-accentBlue/15 text-accentBlue border-accentBlue/30'
                        : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/25 hover:text-white'
                    }`}>
                    {m || 'All'}
                  </button>
                ))}
              </div>
            </div>

            <div className="w-px h-10 bg-white/10 hidden sm:block" />

            {/* Subject + Area */}
            <div className="flex gap-3 flex-1 flex-wrap">
              <div className="relative flex-1 min-w-[140px]">
                <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" size={13} />
                <input
                  type="text"
                  placeholder="Subject"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 py-2.5 pl-9 pr-3 rounded-xl text-sm outline-none focus:border-accentBlue transition-all"
                  style={{ color: '#ffffff', caretColor: '#ffffff' }}
                />
              </div>
              <div className="relative flex-1 min-w-[140px]">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" size={13} />
                <input
                  type="text"
                  placeholder="Area"
                  value={area}
                  onChange={e => setArea(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 py-2.5 pl-9 pr-3 rounded-xl text-sm outline-none focus:border-accentBlue transition-all"
                  style={{ color: '#ffffff', caretColor: '#ffffff' }}
                />
              </div>
            </div>

            {/* Clear */}
            {activeCount > 0 && (
              <button onClick={clearAll}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-red-400 border border-red-400/20 rounded-xl hover:bg-red-400/8 transition-colors self-end">
                <X size={12} /> Clear ({activeCount})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Results ── */}
      <div className="max-w-7xl mx-auto px-6 pb-24">

        {/* Count */}
        {!loading && (
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-gray-400">
              <span className="text-white font-bold">{filtered.length}</span> tutor{filtered.length !== 1 ? 's' : ''} found
              {(search || activeCount > 0) && <span className="text-gray-600"> · filtered</span>}
            </p>
            {activeCount > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-accentBlue">
                <SlidersHorizontal size={12} /> {activeCount} filter{activeCount > 1 ? 's' : ''} active
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 text-gray-500">
            <Loader2 size={32} className="animate-spin text-accentBlue mb-4" />
            <p className="text-sm">Finding tutors…</p>
          </div>
        ) : filtered.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-32 border border-dashed border-white/10 rounded-2xl text-center px-6">
            <GraduationCap size={48} className="text-gray-700 mb-4" />
            <p className="text-white font-bold text-lg mb-2">No tutors found</p>
            <p className="text-gray-500 text-sm mb-6">Try adjusting your search or filters.</p>
            <button onClick={() => { setSearch(''); clearAll(); }}
              className="px-5 py-2.5 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue text-sm font-bold rounded-xl hover:bg-accentBlue/15 transition-colors">
              Clear Everything
            </button>
          </motion.div>
        ) : (
          <>
            <motion.div variants={stagger} initial="hidden" animate="show"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <AnimatePresence>
                {filtered.slice(0, visible).map((tutor, idx) => (
                  <TutorCard key={tutor._id} tutor={tutor} idx={idx} />
                ))}
              </AnimatePresence>
            </motion.div>

            {/* Load more */}
            {filtered.length > visible && (
              <div className="text-center mt-10">
                <button onClick={() => setVisible(v => v + 9)}
                  className="px-8 py-3 bg-white/5 border border-white/10 text-gray-300 text-sm font-bold rounded-xl hover:border-accentBlue/30 hover:text-white transition-all">
                  Load More ({filtered.length - visible} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TutorSearchPage;
